import type { CalendarItem } from './calendar-item';

export interface CalendarBookingActionTarget {
  siteId: string;
  record: string;
  canApprove: boolean;
  canCancel: boolean;
  canCheckIn: boolean;
  canDecline: boolean;
  canMarkNoShow: boolean;
  canReschedule: boolean;
  canMarkPaid: boolean;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isLiveClient = (status?: string) => status !== 'canceled' && status !== 'declined';

export function calendarBookingActionTarget(item: CalendarItem, now: number): CalendarBookingActionTarget | undefined {
  const clients = item.kind === 'booking' && !item.capacity ? item.booking?.clients.filter((client) => isLiveClient(client.status)) : undefined;
  const client = clients?.length === 1 ? clients[0] : undefined;
  const pending = client?.status === 'pending' || client?.status === 'created' || client?.status === 'waiting_list';
  const confirmed = client?.status === 'confirmed';
  if (
    !client?.id ||
    !GUID.test(client.id) ||
    (!pending && !confirmed) ||
    item.settled ||
    !item.ref.siteId ||
    item.ref.type !== 'APPOINTMENT' ||
    item.ref.bookingId !== client.id
  ) return undefined;
  return {
    siteId: item.ref.siteId,
    record: client.id,
    canApprove: pending && (!client.payment || client.payment === 'not_paid'),
    canCancel: confirmed,
    canCheckIn: confirmed && item.start <= now && client.attendance !== 'attended',
    canDecline: pending,
    canMarkNoShow: confirmed && item.end <= now && client.attendance !== 'no_show',
    canReschedule: confirmed,
    canMarkPaid: confirmed && (client.payment === 'not_paid' || client.payment === 'partially_paid'),
  };
}

export function calendarBookingMatchesQuery(item: CalendarItem, query: string): boolean {
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const searchable = [
    item.title,
    item.sourceLabel,
    item.location,
    ...(item.staff ?? []),
    ...(item.people ?? []).map((person) => person.name),
    ...(item.booking?.clients ?? []).map((client) => client.name),
  ]
    .filter((value): value is string => !!value)
    .join('\n')
    .toLocaleLowerCase();
  return terms.every((term) => searchable.includes(term));
}

export type BookingOp = 'bookings.approve' | 'bookings.cancel' | 'bookings.checkIn' | 'bookings.decline' | 'bookings.markNoShow' | 'bookings.markPaid' | 'bookings.reschedule';

/** A booking action that succeeded (or was undone), whose result a read has not shown yet. */
export interface AwaitedBookingOutcome {
  op: BookingOp;
  undone?: boolean;
  /** For a reschedule, the start it moved to. */
  rescheduledTo?: number;
}

/**
 * The buttons an action decides, held back until a read shows its result: the approve/decline pair moves the booking out
 * of pending, check in and no-show both set attendance, a cancel ends every button, and the others decide only their own.
 */
export function heldBookingOps(outcome: AwaitedBookingOutcome): BookingOp[] {
  switch (outcome.op) {
    case 'bookings.approve':
    case 'bookings.decline':
      return ['bookings.approve', 'bookings.decline'];
    case 'bookings.checkIn':
    case 'bookings.markNoShow':
      return ['bookings.checkIn', 'bookings.markNoShow'];
    case 'bookings.cancel':
      return ['bookings.approve', 'bookings.decline', 'bookings.checkIn', 'bookings.markNoShow', 'bookings.markPaid', 'bookings.reschedule', 'bookings.cancel'];
    default:
      return [outcome.op];
  }
}

/** Whether the booking as read shows what the action did to its client `record` (or, undone, that it was reversed). */
export function readShowsBookingOutcome(item: CalendarItem, record: string, outcome: AwaitedBookingOutcome): boolean {
  const client = item.booking?.clients.find((candidate) => candidate.id === record);
  const done = (() => {
    switch (outcome.op) {
      case 'bookings.approve':
        return client?.status === 'confirmed';
      case 'bookings.decline':
        return !client || client.status === 'declined' || client.status === 'canceled';
      case 'bookings.cancel':
        return !client || client.status === 'canceled' || client.status === 'declined';
      case 'bookings.checkIn':
        return client?.attendance === 'attended';
      case 'bookings.markNoShow':
        return client?.attendance === 'no_show';
      case 'bookings.markPaid':
        return client?.payment === 'paid';
      case 'bookings.reschedule':
        return outcome.rescheduledTo !== undefined && item.start === outcome.rescheduledTo;
    }
  })();
  return outcome.undone ? !done : done;
}
